import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { must, useDb } from './db';

/**
 * «Сезон із сюжетом» and «Квест класу»: the class goes through a story together — stations on a
 * map, a mission at each from different subjects, every student with a role in a team. The class
 * moves on when three quarters have done their part; nobody is ranked.
 */
export type SeasonRole = 'engineer' | 'scientist' | 'navigator' | 'chronicler';

export const SEASON_ROLES: ReadonlyArray<{
  key: SeasonRole;
  name: string;
  emoji: string;
  does: string;
}> = [
  { key: 'engineer', name: 'Інженери', emoji: '🔧', does: 'рахують і будують' },
  { key: 'scientist', name: 'Науковці', emoji: '🔬', does: 'досліджують і перевіряють' },
  { key: 'navigator', name: 'Штурмани', emoji: '🧭', does: 'ведуть карти й маршрути' },
  { key: 'chronicler', name: 'Літописці', emoji: '📓', does: 'пишуть і розповідають' },
];

export interface SeasonStation {
  emoji: string;
  title: string;
  story: string;
  tasks: Record<SeasonRole, { subject: string; text: string }>;
}

export interface SeasonStory {
  key: string;
  title: string;
  intro: string;
  emoji: string;
  stations: readonly SeasonStation[];
}

export const SEASONS: readonly SeasonStory[] = [
  {
    key: 'mars',
    title: 'Експедиція на Марс',
    emoji: '🚀',
    intro:
      'Наш клас обрали першим шкільним екіпажем до Марса. Вісім станцій, чотири команди — і долетимо тільки разом.',
    stations: [
      {
        emoji: '🌍',
        title: 'Космодром',
        story: 'Перед стартом треба все порахувати, зібрати й записати: що беремо і навіщо летимо.',
        tasks: {
          engineer: {
            subject: 'Математика',
            text: 'До Марса 225 млн км, ракета летить 30 км/с. Скільки днів триватиме політ?',
          },
          scientist: {
            subject: 'Біологія',
            text: 'Що потрібно людині щодня, щоб жити? Скільки води треба 4 людям на тиждень?',
          },
          navigator: {
            subject: 'Географія',
            text: 'Знайдіть на карті три космодроми світу. Який із них найближчий до України?',
          },
          chronicler: {
            subject: 'Українська мова',
            text: 'Перший запис бортового журналу: хто ми й навіщо летимо — 5 речень.',
          },
        },
      },
      {
        emoji: '🛰️',
        title: 'Орбіта Землі',
        story:
          'Ми на орбіті! Земля внизу крутиться, а ми — разом із Міжнародною космічною станцією.',
        tasks: {
          engineer: {
            subject: 'Математика',
            text: 'МКС облітає Землю за 90 хвилин. Скільки обертів вона робить за добу?',
          },
          scientist: {
            subject: 'Біологія',
            text: 'Як ростуть рослини без сили тяжіння? Знайдіть один дослід, який робили на МКС.',
          },
          navigator: {
            subject: 'Історія України',
            text: 'Хто перший космонавт незалежної України і в якому році він полетів?',
          },
          chronicler: {
            subject: 'Англійська мова',
            text: 'Опишіть Землю з орбіти англійською — три речення.',
          },
        },
      },
      {
        emoji: '🌙',
        title: 'Місяць',
        story: 'Проміжна зупинка: Місяць. Тут усе легше — навіть рюкзак.',
        tasks: {
          engineer: {
            subject: 'Фізика',
            text: 'На Місяці вага вшестеро менша. Скільки «важитиме» рюкзак у 12 кг?',
          },
          scientist: {
            subject: 'Фізика',
            text: 'Чому на Місяці немає вітру і не чути звуку? Поясніть коротко.',
          },
          navigator: {
            subject: 'Географія',
            text: 'Намалюйте фази Місяця на тиждень уперед і підпишіть їх.',
          },
          chronicler: {
            subject: 'Українська література',
            text: 'Знайдіть три українські прислів’я, загадки чи рядки віршів про Місяць.',
          },
        },
      },
      {
        emoji: '☄️',
        title: 'Метеорний потік',
        story: 'Попереду метеорний потік! Треба прокласти безпечний курс і повідомити Землю.',
        tasks: {
          engineer: {
            subject: 'Алгебра',
            text: 'На координатній площині проведіть курс із (0; 0) у (6; 4), оминаючи метеорит у (3; 2).',
          },
          scientist: {
            subject: 'Хімія',
            text: 'З чого складаються метеорити? Чим метеорит відрізняється від астероїда?',
          },
          navigator: {
            subject: 'Географія',
            text: 'Коли з України найкраще видно Персеїди? Куди дивитися?',
          },
          chronicler: {
            subject: 'Англійська мова',
            text: 'Радіограма на Землю англійською: «Пройшли метеорний потік, усі цілі».',
          },
        },
      },
      {
        emoji: '📡',
        title: 'Сигнал із Землі',
        story: 'Рідні надіслали вітання. Але відповідь дійде до них не одразу…',
        tasks: {
          engineer: {
            subject: 'Фізика',
            text: 'Сигнал летить 300 000 км/с. Скільки йде повідомлення на 54,6 млн км?',
          },
          scientist: {
            subject: 'Фізика',
            text: 'Як працює радіо? Поясніть у трьох кроках зі схемою.',
          },
          navigator: {
            subject: 'Географія',
            text: 'Позначте на карті три обсерваторії чи радіотелескопи України.',
          },
          chronicler: {
            subject: 'Українська мова',
            text: 'Відповідь рідним: лист на пів сторінки про перші дні польоту.',
          },
        },
      },
      {
        emoji: '☀️',
        title: 'Сонячна буря',
        story: 'Тривога! Сонячна буря. Екіпаж має за 15 хвилин сховатися в захищений відсік.',
        tasks: {
          engineer: {
            subject: 'Математика',
            text: 'Складіть план на 15 хвилин: хто що робить щохвилини, щоб усі встигли в укриття.',
          },
          scientist: {
            subject: 'Фізика',
            text: 'Що таке сонячний вітер і чому він небезпечний для людей і техніки?',
          },
          navigator: {
            subject: 'Географія',
            text: 'Де на Землі найчастіше видно полярне сяйво і чому саме там?',
          },
          chronicler: {
            subject: 'Українська література',
            text: 'Короткий вірш або хайку про сонячну бурю.',
          },
        },
      },
      {
        emoji: '🔴',
        title: 'Посадка на Марс',
        story: 'Марс! Червоний пил, тонке повітря і гора, вища за Еверест.',
        tasks: {
          engineer: {
            subject: 'Математика',
            text: 'Діаметр Марса — 6 779 км, Землі — 12 742 км. У скільки разів Марс менший?',
          },
          scientist: {
            subject: 'Хімія',
            text: 'Чим дихати на Марсі? Знайдіть склад його атмосфери у відсотках.',
          },
          navigator: {
            subject: 'Географія',
            text: 'Як називається найвища гора Марса і яка її висота?',
          },
          chronicler: {
            subject: 'Українська мова',
            text: 'Перші слова людини на Марсі: придумайте фразу, яку почує вся Земля.',
          },
        },
      },
      {
        emoji: '🏁',
        title: 'База на Марсі',
        story: 'Останній крок — збудувати базу, де можна жити й вирощувати їжу.',
        tasks: {
          engineer: {
            subject: 'Геометрія',
            text: 'Спроєктуйте теплицю 6 × 4 м: площа, периметр, скільки грядок по 1 м².',
          },
          scientist: {
            subject: 'Біологія',
            text: 'Які рослини посадимо першими і чому? Назвіть три й обґрунтуйте.',
          },
          navigator: {
            subject: 'Географія',
            text: 'Намалюйте карту бази: житло, теплиця, енергія, склад.',
          },
          chronicler: {
            subject: 'Українська мова',
            text: 'Фінальний репортаж експедиції для шкільної газети.',
          },
        },
      },
    ],
  },
];

export function seasonStory(key: string): SeasonStory | undefined {
  return SEASONS.find((s) => s.key === key);
}

export interface ClassSeason {
  id: string;
  key: string;
  stage: number;
  stages: number;
  finished: boolean;
  startedAt: string;
  /** the role of each student of the class (plain object: the query cache is saved as JSON) */
  roles: Record<string, SeasonRole>;
}

export function useClassSeason(classId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['class-season', classId],
    enabled: Boolean(classId),
    queryFn: async (): Promise<ClassSeason | null> => {
      const rows = must(
        await db
          .from('class_seasons')
          .select(
            'id, season_key, stage, stages, finished_at, started_at, season_members(student_member_id, role)',
          )
          .eq('class_id', classId as string)
          .order('started_at', { ascending: false })
          .limit(1),
      );
      const r = rows[0];
      if (!r) return null;
      return {
        id: r.id,
        key: r.season_key,
        stage: r.stage,
        stages: r.stages,
        finished: r.finished_at !== null,
        startedAt: r.started_at,
        roles: Object.fromEntries(
          r.season_members.map((m) => [m.student_member_id, m.role as SeasonRole]),
        ),
      };
    },
  });
}

export interface TeamProgress {
  role: SeasonRole;
  members: number;
  done: number;
}

export function useSeasonProgress(seasonId: string | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['season-progress', seasonId],
    enabled: Boolean(seasonId),
    queryFn: async (): Promise<TeamProgress[]> =>
      must(await db.rpc('season_progress', { p_season: seasonId as string }))
        .map((r) => ({ role: r.role as SeasonRole, members: r.members, done: r.done }))
        .sort(
          (a, b) =>
            SEASON_ROLES.findIndex((x) => x.key === a.role) -
            SEASON_ROLES.findIndex((x) => x.key === b.role),
        ),
  });
}

/** Whether these students did their part at the current station (theirs, or the teacher's view). */
export function useSeasonMarks(seasonId: string | undefined, stage: number | undefined) {
  const db = useDb();
  return useQuery({
    queryKey: ['season-marks', seasonId, stage],
    enabled: Boolean(seasonId) && stage !== undefined,
    queryFn: async (): Promise<string[]> =>
      must(
        await db
          .from('season_marks')
          .select('student_member_id')
          .eq('season_id', seasonId as string)
          .eq('stage', stage as number),
      ).map((r) => r.student_member_id),
  });
}

export function useSeasonActions(classId: string | undefined) {
  const db = useDb();
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['class-season', classId] });
    void client.invalidateQueries({ queryKey: ['season-progress'] });
    void client.invalidateQueries({ queryKey: ['season-marks'] });
  };
  return {
    start: useMutation({
      mutationFn: async (story: SeasonStory) =>
        must(
          await db.rpc('start_season', {
            p_class: classId as string,
            p_season: story.key,
            p_stages: story.stations.length,
          }),
        ),
      onSettled: invalidate,
    }),
    done: useMutation({
      mutationFn: async (seasonId: string) => {
        const row = must(await db.rpc('season_done', { p_season: seasonId }))[0];
        return { moved: row?.moved ?? false };
      },
      onSettled: invalidate,
    }),
    next: useMutation({
      mutationFn: async (seasonId: string) =>
        must(await db.rpc('season_next', { p_season: seasonId })),
      onSettled: invalidate,
    }),
  };
}
